import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Notifications as ReminderIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

interface QuickReminderDialogProps {
  open: boolean;
  onClose: () => void;
  onSendMessage: (content: string, agentId?: string) => void;
  conversationId?: string;
}

const QUICK_TIME_OPTIONS = [
  { label: '5 minutes', minutes: 5 },
  { label: '15 minutes', minutes: 15 },
  { label: '1 hour', minutes: 60 },
  { label: '1 day', minutes: 24 * 60 },
  { label: '1 week', minutes: 7 * 24 * 60 },
];

const QuickReminderDialog: React.FC<QuickReminderDialogProps> = ({
  open,
  onClose,
  onSendMessage,
  conversationId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [reminderTitle, setReminderTitle] = useState('');
  const [selectedTime, setSelectedTime] = useState<number | null>(null);

  const handleCreateReminder = () => {
    if (reminderTitle.trim()) {
      let message = `Set a reminder: "${reminderTitle.trim()}"`;

      if (selectedTime !== null) {
        const option = QUICK_TIME_OPTIONS.find(opt => opt.minutes === selectedTime);
        if (option) {
          message += ` in ${option.label}`;
        }
      }

      message += ' - please show me the reminder form to set up the details.';

      onSendMessage(message);

      // Clear and close
      setReminderTitle('');
      setSelectedTime(null);
      onClose();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleCreateReminder();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          bgcolor: 'background.paper',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <ReminderIcon color="secondary" />
          <Typography variant="h6" color="secondary">
            Quick Reminder Setup
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            What would you like to be reminded about? You'll be able to set the exact time and details in the next step.
          </Typography>

          <TextField
            fullWidth
            label="Reminder title"
            value={reminderTitle}
            onChange={(e) => setReminderTitle(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="e.g., Follow up with client, Take medication, Team meeting"
            autoFocus
            multiline
            maxRows={3}
            InputProps={{
              startAdornment: (
                <ReminderIcon style={{ marginRight: 8, color: 'rgba(0,0,0,0.6)', alignSelf: 'flex-start', marginTop: 8 }} />
              ),
            }}
          />

          <div>
            <Typography variant="subtitle2" gutterBottom style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ScheduleIcon fontSize="small" color="secondary" />
              Quick time selection (optional)
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" style={{ gap: 8 }}>
              {QUICK_TIME_OPTIONS.map((option) => (
                <Chip
                  key={option.minutes}
                  label={option.label}
                  variant={selectedTime === option.minutes ? 'filled' : 'outlined'}
                  color={selectedTime === option.minutes ? 'secondary' : 'default'}
                  clickable
                  onClick={() => setSelectedTime(
                    selectedTime === option.minutes ? null : option.minutes
                  )}
                  size="small"
                />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" style={{ marginTop: 8, display: 'block' }}>
              You can customize the exact time in the detailed form
            </Typography>
          </div>

          <div
            style={{
              padding: 16,
              backgroundColor: `${theme.palette.secondary.main}08`,
              borderRadius: 4,
              border: `1px solid ${theme.palette.secondary.main}20`,
            }}
          >
            <Typography variant="body2" color="secondary.main">
              After clicking create, you'll get an interactive reminder form where you can:
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" style={{ marginTop: 8 }}>
              • Set exact date and time<br/>
              • Choose notification channels (chat, email, push)<br/>
              • Set recurring reminders<br/>
              • Add additional message details
            </Typography>
          </div>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleCreateReminder}
          variant="contained"
          color="secondary"
          disabled={!reminderTitle.trim()}
          startIcon={<ReminderIcon />}
        >
          Create Reminder
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickReminderDialog;